"use client";

import { useState, FormEvent } from "react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

type Status = "idle" | "sending" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");

    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.append(
      "access_key",
      process.env.NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY ?? ""
    );

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      const result = await res.json();

      if (result.success) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6 p-8 rounded-[2rem] border border-border/40 bg-card">
      <div className="flex flex-col gap-2">
        <label htmlFor="name" className="font-body text-sm font-medium text-foreground">
          Name
        </label>
        <input
          id="name"
          name="name"
          type="text"
          required
          placeholder="John Doe"
          className="px-4 py-3 rounded-xl border border-border/50 bg-background/50 font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="email" className="font-body text-sm font-medium text-foreground">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          placeholder="john@example.com"
          className="px-4 py-3 rounded-xl border border-border/50 bg-background/50 font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="message" className="font-body text-sm font-medium text-foreground">
          Message
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          required
          placeholder="Tell me about your project..."
          className="px-4 py-3 rounded-xl border border-border/50 bg-background/50 font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={status === "sending"}
        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-60 disabled:pointer-events-none">
        {status === "sending" ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Sending...
          </>
        ) : (
          "Send Message"
        )}
      </button>

      {status === "success" && (
        <div className="flex items-center gap-2 text-sm font-body text-green-600">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <span>Message sent! I&apos;ll get back to you soon.</span>
        </div>
      )}
      {status === "error" && (
        <div className="flex items-center gap-2 text-sm font-body text-destructive">
          <XCircle className="w-5 h-5 shrink-0" />
          <span>Something went wrong. Please try again or email me directly.</span>
        </div>
      )}
    </form>
  );
}
